import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowRight, CheckCircle2, PackagePlus, RotateCcw } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../lib/apiClient';
import { useOrgFormatting } from '../hooks/useOrgFormatting';

type Option = { id: string; name: string };
type PatientOption = { id: string; firstName?: string; lastName?: string; name?: string };
type ServicePackage = {
  id: string;
  patientId: string;
  patientName: string;
  appointmentTypeId: string;
  serviceName: string;
  locationId?: string | null;
  purchasedQuantity: number;
  bonusQuantity: number;
  remainingQuantity: number;
  expiresAt?: string | null;
  externalReference?: string | null;
  purchaseAmount?: number | null;
  currency?: string | null;
  settlementStatus?: string | null;
  onlineSaleEnabled?: boolean;
  taxRate?: number;
  customPackageName?: string | null;
  sessionsPerRedemption?: number;
  status: string;
  redemptionCount: number;
};
type PackageRedemption = {
  id: string;
  packageId: string;
  appointmentId?: string | null;
  billingId?: string | null;
  locationId?: string | null;
  quantity: number;
  serviceName: string;
  redeemedAt: string;
};

const blankForm = {
  patientId: '',
  appointmentTypeId: '',
  locationId: '',
  purchasedQuantity: 4,
  bonusQuantity: 1,
  expiresAt: '',
  externalReference: '',
  purchaseAmount: '',
  taxRate: 0,
  settlementStatus: 'PAID_EXTERNALLY',
  onlineSaleEnabled: false,
  customPackageName: '',
  sessionsPerRedemption: 1,
  status: 'ACTIVE',
  notes: '',
};

function patientName(patient: PatientOption) {
  return patient.name || `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || patient.id;
}

function statusLabel(value?: string | null) {
  return (value || '').replace(/_/g, ' ').toLowerCase() || 'active';
}

export default function PackagesPage() {
  const { fetchJson } = useApiClient();
  // Format money in the org locale + currency (package currency overrides where set).
  const { formatMoney } = useOrgFormatting();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [redemptions, setRedemptions] = useState<PackageRedemption[]>([]);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [form, setForm] = useState(blankForm);
  const [editingPackageId, setEditingPackageId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'online'>('active');
  const [query, setQuery] = useState('');
  const [selectedHistoryPackageId, setSelectedHistoryPackageId] = useState('');
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkExpiry, setBulkExpiry] = useState('');
  const [bulkOnlineSale, setBulkOnlineSale] = useState<'keep' | 'enabled' | 'disabled'>('keep');
  const [bulkTaxRate, setBulkTaxRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const requestedPatientId = searchParams.get('patientId') || '';
  const requestedAppointmentTypeId = searchParams.get('appointmentTypeId') || '';
  const returnTo = searchParams.get('returnTo') || '';

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [packagePayload, redemptionPayload, patientPayload, servicePayload, locationPayload] = await Promise.all([
        fetchJson<{ data: ServicePackage[] }>('/packages'),
        fetchJson<{ data: PackageRedemption[] }>('/packages/redemptions'),
        fetchJson<{ data: PatientOption[] }>('/patients?page=1&pageSize=100'),
        fetchJson<Option[]>('/appointment-types'),
        fetchJson<Option[]>('/locations'),
      ]);
      setPackages(packagePayload.data || []);
      setRedemptions(redemptionPayload.data || []);
      setPatients(patientPayload.data || []);
      setServices(servicePayload.map((service) => ({ id: service.id, name: service.name })));
      setLocations(locationPayload.map((location) => ({ id: location.id, name: location.name })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load packages.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const locationNames = useMemo(() => new Map(locations.map((location) => [location.id, location.name])), [locations]);
  const redemptionsByPackage = useMemo(() => {
    const map = new Map<string, PackageRedemption[]>();
    redemptions.forEach((redemption) => {
      map.set(redemption.packageId, [...(map.get(redemption.packageId) || []), redemption]);
    });
    return map;
  }, [redemptions]);
  const selectedHistoryPackage = useMemo(
    () => packages.find((pkg) => pkg.id === selectedHistoryPackageId) || null,
    [packages, selectedHistoryPackageId],
  );
  const filteredPackages = useMemo(() => {
    const q = query.trim().toLowerCase();
    return packages.filter((pkg) => {
      const status = String(pkg.status || '').toUpperCase();
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && status === 'ACTIVE') ||
        (statusFilter === 'inactive' && status !== 'ACTIVE') ||
        (statusFilter === 'online' && Boolean(pkg.onlineSaleEnabled));
      const matchesQuery = !q || [pkg.patientName, pkg.serviceName, pkg.externalReference, pkg.settlementStatus, pkg.status].some((value) => String(value || '').toLowerCase().includes(q));
      return matchesStatus && matchesQuery;
    });
  }, [packages, query, statusFilter]);

  async function savePackage(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await fetchJson(editingPackageId ? `/packages/${editingPackageId}` : '/packages', {
        method: editingPackageId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          locationId: form.locationId || undefined,
          expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
          externalReference: form.externalReference || undefined,
          purchaseAmount: form.purchaseAmount === '' ? undefined : Number(form.purchaseAmount),
          taxRate: Number(form.taxRate || 0),
          settlementStatus: form.settlementStatus,
          onlineSaleEnabled: form.onlineSaleEnabled,
          customPackageName: form.customPackageName || undefined,
          sessionsPerRedemption: Number(form.sessionsPerRedemption || 1),
          status: form.status,
          notes: form.notes || undefined,
        }),
      });
      setSuccess(editingPackageId ? 'Package updated.' : 'Prepaid package registered. It is now available for package checkout.');
      setEditingPackageId('');
      setForm((prev) => ({
        ...blankForm,
        patientId: requestedPatientId || prev.patientId,
        appointmentTypeId: requestedAppointmentTypeId || prev.appointmentTypeId,
      }));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save package.');
    } finally {
      setSaving(false);
    }
  }

  function editPackage(pkg: ServicePackage) {
    setEditingPackageId(pkg.id);
    setForm({
      patientId: pkg.patientId,
      appointmentTypeId: pkg.appointmentTypeId,
      locationId: pkg.locationId || '',
      purchasedQuantity: pkg.purchasedQuantity,
      bonusQuantity: pkg.bonusQuantity,
      expiresAt: pkg.expiresAt ? pkg.expiresAt.slice(0, 10) : '',
      externalReference: pkg.externalReference || '',
      purchaseAmount: pkg.purchaseAmount == null ? '' : String(pkg.purchaseAmount),
      taxRate: Number(pkg.taxRate || 0),
      settlementStatus: pkg.settlementStatus || 'PAID_EXTERNALLY',
      onlineSaleEnabled: Boolean(pkg.onlineSaleEnabled),
      customPackageName: pkg.customPackageName || '',
      sessionsPerRedemption: Number(pkg.sessionsPerRedemption || 1),
      status: pkg.status || 'ACTIVE',
      notes: '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function deactivatePackage(pkg: ServicePackage) {
    if (!window.confirm('Deactivate this package?')) return;
    setError('');
    try {
      await fetchJson(`/packages/${pkg.id}`, { method: 'DELETE' });
      setSuccess('Package deactivated.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to deactivate package.');
    }
  }

  function resetForm() {
    setEditingPackageId('');
    setForm(blankForm);
  }

  async function updatePackageQuick(pkg: ServicePackage, patch: Partial<typeof blankForm>) {
    setError('');
    try {
      await fetchJson(`/packages/${pkg.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId: pkg.patientId,
          appointmentTypeId: pkg.appointmentTypeId,
          locationId: pkg.locationId || undefined,
          purchasedQuantity: pkg.purchasedQuantity,
          bonusQuantity: pkg.bonusQuantity,
          expiresAt: pkg.expiresAt || undefined,
          externalReference: pkg.externalReference || undefined,
          purchaseAmount: pkg.purchaseAmount == null ? undefined : pkg.purchaseAmount,
          taxRate: Number(pkg.taxRate || 0),
          settlementStatus: pkg.settlementStatus || 'PAID_EXTERNALLY',
          onlineSaleEnabled: Boolean(pkg.onlineSaleEnabled),
          customPackageName: pkg.customPackageName || undefined,
          sessionsPerRedemption: Number(pkg.sessionsPerRedemption || 1),
          status: pkg.status || 'ACTIVE',
          ...patch,
        }),
      });
      setSuccess('Package updated.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update package.');
    }
  }

  async function extendPackage(pkg: ServicePackage) {
    const current = pkg.expiresAt ? pkg.expiresAt.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const nextExpiry = window.prompt('New expiry date (YYYY-MM-DD)', current);
    if (!nextExpiry) return;
    await updatePackageQuick(pkg, { expiresAt: nextExpiry, status: 'ACTIVE' });
  }

  async function reactivatePackage(pkg: ServicePackage) {
    await updatePackageQuick(pkg, { status: 'ACTIVE' });
  }

  function togglePackageSelection(id: string, checked: boolean) {
    setSelectedPackageIds((current) => checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id));
  }

  async function applyBulkEdit() {
    const ids = selectedPackageIds.length ? selectedPackageIds : filteredPackages.map((pkg) => pkg.id);
    const payload: Record<string, unknown> = { ids };
    if (bulkStatus) payload.status = bulkStatus;
    if (bulkExpiry) payload.expiresAt = new Date(bulkExpiry).toISOString();
    if (bulkOnlineSale !== 'keep') payload.onlineSaleEnabled = bulkOnlineSale === 'enabled';
    if (bulkTaxRate !== '') payload.taxRate = Number(bulkTaxRate || 0);
    if (Object.keys(payload).length <= 1) return;
    setError('');
    try {
      await fetchJson('/packages/bulk', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSuccess('Bulk package update complete.');
      setSelectedPackageIds([]);
      setBulkStatus('');
      setBulkExpiry('');
      setBulkOnlineSale('keep');
      setBulkTaxRate('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update packages.');
    }
  }

  async function voidRedemption(redemption: PackageRedemption) {
    if (!window.confirm('Void this package session redemption?')) return;
    setError('');
    try {
      await fetchJson(`/packages/redemptions/${redemption.id}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Voided from package usage history' }),
      });
      setSuccess('Package session voided.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to void package session.');
    }
  }

  async function refundRemaining(pkg: ServicePackage) {
    if (!window.confirm(`Refund ${pkg.remainingQuantity} remaining session(s)?`)) return;
    setError('');
    try {
      await fetchJson(`/packages/${pkg.id}/refund-remaining`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Refunded from package workspace' }),
      });
      setSuccess('Remaining package sessions refunded.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to refund remaining sessions.');
    }
  }

  async function transferSession(pkg: ServicePackage) {
    const targetPatientId = window.prompt('Target client ID');
    if (!targetPatientId?.trim()) return;
    const quantity = Math.max(1, Number(window.prompt('Sessions to transfer', '1') || 1));
    setError('');
    try {
      await fetchJson(`/packages/${pkg.id}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPatientId: targetPatientId.trim(), quantity, notes: 'Transferred from package workspace' }),
      });
      setSuccess('Package session transferred.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to transfer package session.');
    }
  }

  function repeatPackageAppointment(pkg: ServicePackage) {
    const params = new URLSearchParams({
      patientId: pkg.patientId,
      appointmentTypeId: pkg.appointmentTypeId,
      packageId: pkg.id,
      packageBookingEnabled: 'true',
      repeatEnabled: 'true',
    });
    window.location.href = `/appointments?${params.toString()}`;
  }

  useEffect(() => {
    if (!requestedPatientId && !requestedAppointmentTypeId) return;
    setForm((prev) => ({
      ...prev,
      patientId: requestedPatientId || prev.patientId,
      appointmentTypeId: requestedAppointmentTypeId || prev.appointmentTypeId,
    }));
  }, [requestedAppointmentTypeId, requestedPatientId]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Offers &amp; Packages</p>
          <h1 className="text-2xl font-semibold text-slate-900">Prepaid packages</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Register prepaid service balances here, then redeem them from appointment checkout or the visit workspace when care is delivered.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {returnTo ? (
            <Link to={returnTo} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">
              Return to checkout
              <ArrowRight size={16} />
            </Link>
          ) : null}
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium">
            <RotateCcw size={16} />
            Refresh
          </button>
        </div>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {success ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} /> {success}</span>
          {returnTo ? <Link to={returnTo} className="font-semibold underline">Return to checkout</Link> : null}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <MetricCard label="Active packages" value={packages.filter((pkg) => pkg.status === 'ACTIVE').length} />
        <MetricCard label="Remaining visits" value={packages.reduce((sum, pkg) => sum + Number(pkg.remainingQuantity || 0), 0)} />
        <MetricCard label="Online packages" value={packages.filter((pkg) => pkg.onlineSaleEnabled).length} />
      </div>

      {(requestedPatientId || requestedAppointmentTypeId) ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          This package form was opened from appointment checkout, so the patient and service are preselected. Save the package, then return to checkout to redeem it.
        </div>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <form onSubmit={savePackage} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <PackagePlus size={18} />
                {editingPackageId ? 'Edit prepaid package' : 'Register prepaid package'}
              </div>
              <p className="mt-1 text-sm text-slate-600">Create the prepaid balance first, then redeem one unit from checkout or the visit workspace when care is delivered.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{Number(form.purchasedQuantity || 0) + Number(form.bonusQuantity || 0)} total units</span>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Patient">
              <select required value={form.patientId} onChange={(event) => setForm((prev) => ({ ...prev, patientId: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2">
                <option value="">Select patient</option>
                {patients.map((patient) => <option key={patient.id} value={patient.id}>{patientName(patient)}</option>)}
              </select>
            </Field>
            <Field label="Service">
              <select required value={form.appointmentTypeId} onChange={(event) => setForm((prev) => ({ ...prev, appointmentTypeId: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2">
                <option value="">Select service</option>
                {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
              </select>
            </Field>
            <Field label="Where package can be used">
              <select value={form.locationId} onChange={(event) => setForm((prev) => ({ ...prev, locationId: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2">
                <option value="">Any permitted location</option>
                {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
              </select>
            </Field>
            <Field label="Purchased quantity"><input type="number" min={1} value={form.purchasedQuantity} onChange={(event) => setForm((prev) => ({ ...prev, purchasedQuantity: Math.max(1, Number(event.target.value || 1)) }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
            <Field label="Bonus quantity"><input type="number" min={0} value={form.bonusQuantity} onChange={(event) => setForm((prev) => ({ ...prev, bonusQuantity: Math.max(0, Number(event.target.value || 0)) }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
            <Field label="Expiry date"><input type="date" value={form.expiresAt} onChange={(event) => setForm((prev) => ({ ...prev, expiresAt: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Package amount"><input type="number" min={0} step="0.01" placeholder="Optional" value={form.purchaseAmount} onChange={(event) => setForm((prev) => ({ ...prev, purchaseAmount: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
            <Field label="Package tax %"><input type="number" min={0} step="0.01" value={form.taxRate} onChange={(event) => setForm((prev) => ({ ...prev, taxRate: Number(event.target.value || 0) }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
            <Field label="Payment state">
              <select value={form.settlementStatus} onChange={(event) => setForm((prev) => ({ ...prev, settlementStatus: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2">
                <option value="PAID_EXTERNALLY">Already paid externally</option>
                <option value="INVOICED">Invoice/reference sent</option>
                <option value="UNPAID">Not paid yet</option>
                <option value="COMPLIMENTARY">Complimentary</option>
              </select>
            </Field>
            <Field label="Package status">
              <select value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2">
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DEPLETED">Depleted</option>
                <option value="EXPIRED">Expired</option>
              </select>
            </Field>
            <Field label="Online package sales">
              <label className="flex min-h-[42px] items-center gap-2 rounded-md border border-slate-300 px-3 py-2">
                <input type="checkbox" checked={form.onlineSaleEnabled} onChange={(event) => setForm((prev) => ({ ...prev, onlineSaleEnabled: event.target.checked }))} className="h-4 w-4" />
                <span>Available for online purchase</span>
              </label>
            </Field>
            <Field label="Custom package name"><input placeholder="Optional package display name" value={form.customPackageName} onChange={(event) => setForm((prev) => ({ ...prev, customPackageName: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
            <Field label="Sessions per redemption"><input type="number" min={1} value={form.sessionsPerRedemption} onChange={(event) => setForm((prev) => ({ ...prev, sessionsPerRedemption: Math.max(1, Number(event.target.value || 1)) }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
            <Field label="Receipt or reference number"><input placeholder="Receipt, invoice, bank transfer, or payment link reference" value={form.externalReference} onChange={(event) => setForm((prev) => ({ ...prev, externalReference: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
            <Field label="Notes"><input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3">
            <p className="text-sm text-slate-600">Total units created: <span className="font-semibold text-slate-900">{Number(form.purchasedQuantity || 0) + Number(form.bonusQuantity || 0)}</span></p>
            <div className="flex gap-2">
            {editingPackageId ? <button type="button" onClick={resetForm} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold">Cancel edit</button> : null}
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              <PackagePlus size={16} />
              {saving ? 'Saving...' : editingPackageId ? 'Update package' : 'Save prepaid package'}
            </button>
            </div>
          </div>
        </form>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Draft package summary</div>
            <div className="mt-4 space-y-3 text-sm">
              <SummaryRow label="Patient" value={patients.find((patient) => patient.id === form.patientId) ? patientName(patients.find((patient) => patient.id === form.patientId) as PatientOption) : 'Not selected'} />
              <SummaryRow label="Service" value={services.find((service) => service.id === form.appointmentTypeId)?.name || 'Not selected'} />
              <SummaryRow label="Package name" value={form.customPackageName || services.find((service) => service.id === form.appointmentTypeId)?.name || 'Not selected'} />
              <SummaryRow label="Location" value={locations.find((location) => location.id === form.locationId)?.name || 'Any permitted location'} />
              <SummaryRow label="Units" value={String(Number(form.purchasedQuantity || 0) + Number(form.bonusQuantity || 0))} />
              <SummaryRow label="Tax" value={form.purchaseAmount ? formatMoney(Number(form.purchaseAmount || 0) * (Number(form.taxRate || 0) / 100)) : `${Number(form.taxRate || 0)}%`} />
              <SummaryRow label="Session calculation" value={`${form.sessionsPerRedemption} session${Number(form.sessionsPerRedemption || 1) === 1 ? '' : 's'} per redemption`} />
              <SummaryRow label="Payment" value={statusLabel(form.settlementStatus)} />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">How this works</div>
            <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
              <p>1. Register the prepaid balance here.</p>
              <p>2. Return to appointment checkout or the visit workspace.</p>
              <p>3. Redeem one unit when the service is delivered.</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/offers" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Offers workspace</Link>
              {returnTo ? <Link to={returnTo} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Return to checkout</Link> : null}
            </div>
          </div>
        </aside>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="text-sm font-semibold text-slate-900">Package balances</div>
          <div className="flex flex-wrap gap-2">
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search packages..." className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'inactive' | 'online')} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
              <option value="active">Active</option>
              <option value="inactive">Inactive or depleted</option>
              <option value="online">Online sales</option>
              <option value="all">All packages</option>
            </select>
          </div>
        </div>
        <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Bulk Package Editor</div>
              <div className="text-xs text-slate-500">{selectedPackageIds.length || filteredPackages.length} package(s) targeted</div>
            </div>
            <div className="grid flex-1 gap-2 md:grid-cols-5">
              <select value={bulkStatus} onChange={(event) => setBulkStatus(event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                <option value="">Keep status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DEPLETED">Depleted</option>
                <option value="EXPIRED">Expired</option>
              </select>
              <input type="date" value={bulkExpiry} onChange={(event) => setBulkExpiry(event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <select value={bulkOnlineSale} onChange={(event) => setBulkOnlineSale(event.target.value as 'keep' | 'enabled' | 'disabled')} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                <option value="keep">Keep online sale</option>
                <option value="enabled">Enable online sale</option>
                <option value="disabled">Disable online sale</option>
              </select>
              <input type="number" min={0} step="0.01" value={bulkTaxRate} onChange={(event) => setBulkTaxRate(event.target.value)} placeholder="Tax %" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <button type="button" onClick={() => void applyBulkEdit()} className="rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white">Apply bulk edit</button>
            </div>
          </div>
        </div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={Boolean(filteredPackages.length) && filteredPackages.every((pkg) => selectedPackageIds.includes(pkg.id))}
                  onChange={(event) => setSelectedPackageIds(event.target.checked ? filteredPackages.map((pkg) => pkg.id) : [])}
                  aria-label="Select all packages"
                />
              </th>
              <th className="px-4 py-3">Patient</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Remaining</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td className="px-4 py-5 text-slate-500" colSpan={7}>Loading packages...</td></tr> : filteredPackages.length ? filteredPackages.map((pkg) => (
              <tr key={pkg.id}>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedPackageIds.includes(pkg.id)}
                    onChange={(event) => togglePackageSelection(pkg.id, event.target.checked)}
                    aria-label={`Select ${pkg.customPackageName || pkg.serviceName}`}
                  />
                </td>
                <td className="px-4 py-3"><div className="font-semibold text-slate-900">{pkg.patientName}</div><div className="text-slate-500">{pkg.externalReference || 'No reference recorded'}</div></td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{pkg.customPackageName || pkg.serviceName}</div>
                  <div className="text-xs text-slate-500">{pkg.locationId ? locationNames.get(pkg.locationId) || pkg.locationId : 'Any permitted location'}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{pkg.remainingQuantity} of {pkg.purchasedQuantity + pkg.bonusQuantity} · {pkg.redemptionCount} redeemed</div>
                  <div className="text-xs text-slate-500">Used {pkg.purchasedQuantity + pkg.bonusQuantity - pkg.remainingQuantity} · {Number(pkg.sessionsPerRedemption || 1)} session/redemption</div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{statusLabel(pkg.settlementStatus)}</div>
                  <div className="text-xs text-slate-500">{pkg.purchaseAmount != null ? formatMoney(pkg.purchaseAmount, pkg.currency || undefined) : 'No amount recorded'}</div>
                  <div className="text-xs text-slate-500">Tax {Number(pkg.taxRate || 0)}%</div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{pkg.status}{pkg.expiresAt ? ` · expires ${new Date(pkg.expiresAt).toLocaleDateString()}` : ''}</div>
                  <div className="text-xs text-slate-500">{pkg.onlineSaleEnabled ? 'Online purchase enabled' : 'Staff sale only'}</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-wrap justify-end gap-2">
                    <button type="button" onClick={() => setSelectedHistoryPackageId(selectedHistoryPackageId === pkg.id ? '' : pkg.id)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">History</button>
                    <button type="button" onClick={() => repeatPackageAppointment(pkg)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Repeat</button>
                    <button type="button" onClick={() => void transferSession(pkg)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Transfer</button>
                    <button type="button" onClick={() => void refundRemaining(pkg)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Refund remaining</button>
                    <button type="button" onClick={() => void extendPackage(pkg)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Extend</button>
                    {pkg.status !== 'ACTIVE' ? <button type="button" onClick={() => void reactivatePackage(pkg)} className="rounded-md border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">Reactivate</button> : null}
                    <button type="button" onClick={() => editPackage(pkg)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Edit</button>
                    <button type="button" onClick={() => deactivatePackage(pkg)} className="rounded-md border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50">Delete</button>
                  </div>
                </td>
              </tr>
            )) : <tr><td className="px-4 py-5 text-slate-500" colSpan={7}>No packages found.</td></tr>}
          </tbody>
        </table>
      </div>

      {selectedHistoryPackage ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Package usage history</div>
              <div className="text-xs text-slate-500">{selectedHistoryPackage.customPackageName || selectedHistoryPackage.serviceName} · {selectedHistoryPackage.patientName}</div>
            </div>
            <button type="button" onClick={() => setSelectedHistoryPackageId('')} className="text-sm text-slate-500 hover:text-slate-900">Close</button>
          </div>
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Quantity</th><th className="px-4 py-3">Sessions used</th><th className="px-4 py-3">Appointment</th><th className="px-4 py-3">Billing</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(redemptionsByPackage.get(selectedHistoryPackage.id) || []).map((redemption) => (
                <tr key={redemption.id}>
                  <td className="px-4 py-3 text-slate-600">{new Date(redemption.redeemedAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-700">{redemption.serviceName}</td>
                  <td className="px-4 py-3 text-slate-600">{redemption.quantity}</td>
                  <td className="px-4 py-3 text-slate-600">{redemption.quantity * Number(selectedHistoryPackage.sessionsPerRedemption || 1)}</td>
                  <td className="px-4 py-3 text-slate-600">{redemption.appointmentId || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{redemption.billingId || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => void voidRedemption(redemption)} className="rounded-md border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50">Void session</button>
                  </td>
                </tr>
              ))}
              {!(redemptionsByPackage.get(selectedHistoryPackage.id) || []).length ? <tr><td className="px-4 py-5 text-slate-500" colSpan={7}>No redemptions for this package yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Recent package redemptions</div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <tbody className="divide-y divide-slate-100">
            {redemptions.length ? redemptions.map((redemption) => (
              <tr key={redemption.id}>
                <td className="px-4 py-3 text-slate-700">{redemption.serviceName}</td>
                <td className="px-4 py-3 text-slate-600">{redemption.quantity} used</td>
                <td className="px-4 py-3 text-slate-600">{redemption.appointmentId || 'No appointment'}</td>
                <td className="px-4 py-3 text-slate-600">{new Date(redemption.redeemedAt).toLocaleDateString()}</td>
              </tr>
            )) : <tr><td className="px-4 py-5 text-slate-500">No package redemptions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-900">{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
